import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, FileText, Package, Truck, CheckCircle, Send, MoreVertical, Eye, Calendar, DollarSign, Building2, Download, Upload, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  getPurchaseOrders, createPurchaseOrder, approvePurchaseOrder, 
  sendPurchaseOrder, confirmPurchaseOrder, completePurchaseOrder,
  getSuppliers, getCompanies, getContracts,
  downloadPurchaseOrderPDF, uploadReceivedPO, getReceivedPOs,
  getPurchaseRequests, provideSupplierRates
} from '../lib/api';

// Default asset types - can be extended with custom types
const DEFAULT_ASSET_TYPES = [
  'Pickup', 'Truck', 'Bus', 'Excavator', 'Bulldozer', 'Crane', 'Loader', 
  'Forklift', 'Trailer', 'Tanker', 'Compactor', 'Grader', 'Roller'
];

// Standard payment terms
const STANDARD_PAYMENT_TERMS = [
  { value: 'advance', label: 'Advance Payment' },
  { value: 'immediate', label: 'Immediate / On Delivery' },
  { value: '15', label: '15 Days' },
  { value: '30', label: '30 Days' },
  { value: '45', label: '45 Days' },
  { value: '60', label: '60 Days' },
  { value: '90', label: '90 Days' },
  { value: 'custom', label: 'Custom Days' }
];

const emptyPO = {
  company_id: '',
  supplier_id: '',
  contract_id: '',
  items: [],
  start_date: '',
  end_date: '',
  delivery_location: '',
  payment_terms: '30',
  custom_payment_days: '',
  additional_terms: '',
  notes: ''
};

const emptyItem = { description: '', asset_type: '', custom_asset_type: '', quantity: 1, unit_rate: 0, total: 0, duration_days: 30, with_operator: false, operator_rate: 0 };

export const PurchaseOrdersPage = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [receivedPOs, setReceivedPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('supplier');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [formData, setFormData] = useState(emptyPO);
  const [customAssetTypes, setCustomAssetTypes] = useState([]);
  const [newCustomAssetType, setNewCustomAssetType] = useState('');
  
  // Purchase Requests (incoming from sales/enquiries)
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [isRatesDialogOpen, setIsRatesDialogOpen] = useState(false);
  const [selectedPR, setSelectedPR] = useState(null);
  const [ratesForm, setRatesForm] = useState({ supplier_id: '', supplier_name: '', daily_rate: '', monthly_rate: '', mobilization: '', notes: '' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (companyFilter !== 'all') filters.company_id = companyFilter;
      if (supplierFilter !== 'all') filters.supplier_id = supplierFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      
      const [posRes, suppliersRes, companiesRes, contractsRes] = await Promise.all([
        getPurchaseOrders(
          filters.company_id || null, 
          filters.supplier_id || null, 
          filters.status || null
        ),
        getSuppliers(),
        getCompanies(),
        getContracts()
      ]);
      setPurchaseOrders(posRes.data);
      setSuppliers(suppliersRes.data);
      setCompanies(companiesRes.data);
      setContracts(contractsRes.data);
      
      // Load received POs (client-issued POs)
      try {
        const receivedRes = await getReceivedPOs(filters.company_id || null);
        setReceivedPOs(receivedRes.data || []);
      } catch (e) {
        // If API not available yet, set empty
        setReceivedPOs([]);
      }
      
      // Load incoming purchase requests (from enquiries needing supplier sourcing)
      try {
        const prRes = await getPurchaseRequests(filters.company_id || null);
        setPurchaseRequests(prRes.data || []);
      } catch (e) {
        setPurchaseRequests([]);
      }
    } catch (error) {
      toast.error('Failed to load purchase orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [companyFilter, supplierFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = () => {
    setSelectedPO(null);
    setFormData({ 
      ...emptyPO, 
      company_id: companies[0]?.id || '',
      start_date: new Date().toISOString().split('T')[0]
    });
    setIsDialogOpen(true);
  };

  const handleView = (po) => {
    setSelectedPO(po);
    setIsViewDialogOpen(true);
  };

  const openRatesDialog = (pr) => {
    setSelectedPR(pr);
    setRatesForm({ supplier_id: '', supplier_name: '', daily_rate: '', monthly_rate: '', mobilization: '', notes: '' });
    setIsRatesDialogOpen(true);
  };

  const handleProvideRates = async () => {
    const typedName = (ratesForm.supplier_name || '').trim();
    if (!ratesForm.supplier_id && !typedName) {
      toast.error('Please select or type a supplier name');
      return;
    }
    if (!ratesForm.daily_rate && !ratesForm.monthly_rate) {
      toast.error('Enter at least one rate (daily or monthly)');
      return;
    }
    const supplier = suppliers.find(s => s.id === ratesForm.supplier_id);
    const supplierName = supplier?.name || typedName;
    try {
      await provideSupplierRates(selectedPR.id, {
        supplier_rates: [{
          supplier_id: ratesForm.supplier_id || null,
          supplier_name: supplierName,
          daily_rate: parseFloat(ratesForm.daily_rate) || 0,
          monthly_rate: parseFloat(ratesForm.monthly_rate) || 0,
          mobilization: parseFloat(ratesForm.mobilization) || 0
        }],
        notes: ratesForm.notes
      });
      toast.success('Rates provided — sales team will see them on the enquiry');
      setIsRatesDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to provide rates');
    }
  };

  // Get suppliers filtered by selected company
  const getFilteredSuppliers = () => {
    if (!formData.company_id) return [];
    return suppliers.filter(s => {
      // Filter by status and company
      if (s.status !== 'active') return false;
      // If supplier has company_id array, check if it includes selected company
      if (s.company_ids && Array.isArray(s.company_ids)) {
        return s.company_ids.includes(formData.company_id);
      }
      // If supplier has single company_id, check if it matches
      if (s.company_id) {
        return s.company_id === formData.company_id;
      }
      // If no company restriction, show all active suppliers
      return true;
    });
  };

  const handleSubmit = async () => {
    if (!formData.company_id || !formData.supplier_id || formData.items.length === 0) {
      toast.error('Please fill in required fields and add at least one item');
      return;
    }

    // Build payment terms string
    let paymentTerms = formData.payment_terms;
    if (formData.payment_terms === 'custom' && formData.custom_payment_days) {
      paymentTerms = `${formData.custom_payment_days} days`;
    } else if (formData.payment_terms === 'advance') {
      paymentTerms = 'Advance Payment';
    } else if (formData.payment_terms === 'immediate') {
      paymentTerms = 'Immediate / On Delivery';
    } else if (!isNaN(parseInt(formData.payment_terms))) {
      paymentTerms = `${formData.payment_terms} days`;
    }

    // Process items - handle custom asset types
    const processedItems = formData.items.map(item => ({
      ...item,
      asset_type: item.asset_type === 'custom' ? item.custom_asset_type : item.asset_type
    }));

    try {
      const poData = {
        ...formData,
        items: processedItems,
        payment_terms: paymentTerms,
        additional_terms: formData.additional_terms
      };
      
      const response = await createPurchaseOrder(poData);
      toast.success('Purchase Order created');
      setIsDialogOpen(false);
      loadData();
      
      // Offer to download PDF
      if (response.data?.id) {
        const shouldDownload = window.confirm('Purchase Order created! Would you like to download it as PDF?');
        if (shouldDownload) {
          handleDownloadPDF(response.data);
        }
      }
    } catch (error) {
      toast.error('Failed to create Purchase Order');
      console.error(error);
    }
  };

  const handleApprove = async (po) => {
    try {
      await approvePurchaseOrder(po.id);
      toast.success('Purchase Order approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve Purchase Order');
    }
  };

  const handleSend = async (po) => {
    try {
      await sendPurchaseOrder(po.id);
      toast.success('Purchase Order sent to supplier');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Purchase Order');
    }
  };

  const handleConfirm = async (po) => {
    try {
      await confirmPurchaseOrder(po.id);
      toast.success('Purchase Order confirmed');
      loadData();
    } catch (error) {
      toast.error('Failed to confirm Purchase Order');
    }
  };

  const handleComplete = async (po) => {
    try {
      await completePurchaseOrder(po.id);
      toast.success('Purchase Order completed');
      loadData();
    } catch (error) {
      toast.error('Failed to complete Purchase Order');
    }
  };

  const handleDownloadPDF = async (po) => {
    try {
      const response = await downloadPurchaseOrderPDF(po.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${po.po_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
      console.error(error);
    }
  };

  const handleUploadReceivedPO = async (file) => {
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('company_id', companyFilter !== 'all' ? companyFilter : companies[0]?.id);
    
    try {
      await uploadReceivedPO(formDataUpload);
      toast.success('Client PO uploaded successfully');
      setIsUploadDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to upload PO');
      console.error(error);
    }
  };

  // Asset type management
  const getAllAssetTypes = () => {
    return [...DEFAULT_ASSET_TYPES, ...customAssetTypes];
  };

  const addCustomAssetType = () => {
    if (newCustomAssetType.trim() && !getAllAssetTypes().includes(newCustomAssetType.trim())) {
      setCustomAssetTypes([...customAssetTypes, newCustomAssetType.trim()]);
      setNewCustomAssetType('');
      toast.success('Custom asset type added');
    }
  };

  // Item management
  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, { ...emptyItem }] });
  };
  
  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };
  
  const updateItem = (index, field, value) => {
    const updated = [...formData.items];
    updated[index] = { ...updated[index], [field]: value };
    // Auto-calculate total
    if (field === 'quantity' || field === 'unit_rate') {
      updated[index].total = updated[index].quantity * updated[index].unit_rate;
    }
    setFormData({ ...formData, items: updated });
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      sent: 'bg-purple-100 text-purple-800',
      confirmed: 'bg-indigo-100 text-indigo-800',
      in_progress: 'bg-cyan-100 text-cyan-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={variants[status] || variants.draft}>{status?.replace('_', ' ')}</Badge>;
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.name || 'Unknown';
  };

  const filteredPOs = purchaseOrders.filter(po => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return po.po_number?.toLowerCase().includes(term) || 
           po.supplier_name?.toLowerCase().includes(term);
  });

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((acc, item) => acc + (item.total || 0), 0);
    const vatAmount = subtotal * 0.15;
    return { subtotal, vatAmount, total: subtotal + vatAmount };
  };

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Orders</h1>
          <p className="text-muted-foreground mt-1">Manage supplier and client purchase orders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsUploadDialogOpen(true)} data-testid="upload-received-po-btn">
            <Upload className="h-4 w-4 mr-2" />
            Upload Client PO
          </Button>
          <Button onClick={handleCreate} data-testid="create-po-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Supplier PO
          </Button>
        </div>
      </div>

      {/* Tabs for Supplier POs vs Received Client POs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="supplier">Supplier POs (Outgoing)</TabsTrigger>
          <TabsTrigger value="received">Client POs (Received)</TabsTrigger>
          <TabsTrigger value="requests" data-testid="purchase-requests-tab">
            Purchase Requests
            {purchaseRequests.filter(r => r.status === 'pending').length > 0 && (
              <Badge className="ml-2 bg-red-100 text-red-800">{purchaseRequests.filter(r => r.status === 'pending').length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="supplier" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by PO number or supplier..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-po"
                  />
                </div>
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="company-filter">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All Suppliers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Suppliers</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending_approval">Pending Approval</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{purchaseOrders.length}</p>
                    <p className="text-sm text-muted-foreground">Total POs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <Package className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {purchaseOrders.filter(po => po.status === 'draft' || po.status === 'pending_approval').length}
                    </p>
                    <p className="text-sm text-muted-foreground">Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {purchaseOrders.filter(po => po.status === 'confirmed' || po.status === 'in_progress').length}
                    </p>
                    <p className="text-sm text-muted-foreground">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {purchaseOrders.reduce((acc, po) => acc + (po.total || 0), 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Value (SAR)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* PO List */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading purchase orders...</div>
          ) : filteredPOs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold">No purchase orders found</h3>
                <p className="text-muted-foreground mt-1">Create your first purchase order to get started</p>
                <Button className="mt-4" onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create PO
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredPOs.map((po) => (
                <Card key={po.id} className="hover:shadow-md transition-shadow" data-testid={`po-card-${po.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div>
                          <p className="font-semibold text-lg">{po.po_number}</p>
                          <p className="text-sm text-muted-foreground">{po.supplier_name}</p>
                          <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                            <Building2 className="h-3 w-3" />
                            {getCompanyName(po.company_id)}
                          </p>
                        </div>
                        {getStatusBadge(po.status)}
                      </div>
                      
                      <div className="flex items-center gap-8">
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Start Date</p>
                          <p className="font-medium">{new Date(po.start_date).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Total</p>
                          <p className="font-semibold text-lg">SAR {po.total?.toLocaleString()}</p>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleView(po)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadPDF(po)}>
                              <Download className="h-4 w-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            {po.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleApprove(po)}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </DropdownMenuItem>
                            )}
                            {po.status === 'approved' && (
                              <DropdownMenuItem onClick={() => handleSend(po)}>
                                <Send className="h-4 w-4 mr-2" />
                                Send to Supplier
                              </DropdownMenuItem>
                            )}
                            {po.status === 'sent' && (
                              <DropdownMenuItem onClick={() => handleConfirm(po)}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Mark Confirmed
                              </DropdownMenuItem>
                            )}
                            {(po.status === 'confirmed' || po.status === 'in_progress') && (
                              <DropdownMenuItem onClick={() => handleComplete(po)}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Mark Complete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="received" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Received Client Purchase Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {receivedPOs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No client purchase orders uploaded yet.</p>
                  <p className="text-sm mt-2">Upload client-issued POs to track received orders.</p>
                  <Button className="mt-4" variant="outline" onClick={() => setIsUploadDialogOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Client PO
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {receivedPOs.map((po) => (
                    <Card key={po.id} className="bg-muted/30">
                      <CardContent className="py-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{po.filename || po.po_number}</p>
                            <p className="text-sm text-muted-foreground">
                              Received: {new Date(po.uploaded_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                              <Building2 className="h-3 w-3" />
                              {getCompanyName(po.company_id)}
                            </p>
                          </div>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Purchase Requests from Sales / Enquiries ({purchaseRequests.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {purchaseRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-purchase-requests">
                  No purchase requests. Requests appear here automatically when an enquiry needs supplier-sourced equipment.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Requested</th>
                        <th className="pb-3 font-medium">Client</th>
                        <th className="pb-3 font-medium">Asset Type</th>
                        <th className="pb-3 font-medium">Qty</th>
                        <th className="pb-3 font-medium">Duration</th>
                        <th className="pb-3 font-medium">Project</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseRequests.map((pr) => (
                        <tr key={pr.id} className="border-b hover:bg-muted/50" data-testid={`purchase-request-row-${pr.id}`}>
                          <td className="py-3">{pr.requested_at ? new Date(pr.requested_at).toLocaleDateString() : '-'}</td>
                          <td className="py-3 font-medium">{pr.client_name || '-'}</td>
                          <td className="py-3 capitalize">{pr.asset_type} {pr.with_operator && <Badge variant="outline" className="text-xs ml-1">w/ Operator</Badge>}</td>
                          <td className="py-3">{pr.quantity}</td>
                          <td className="py-3">{pr.duration_months ? `${pr.duration_months} mo` : '-'}</td>
                          <td className="py-3">{pr.project_name || '-'}</td>
                          <td className="py-3">
                            <Badge className={pr.status === 'pending' ? 'bg-amber-100 text-amber-800' : pr.status === 'rates_provided' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {pr.status === 'rates_provided' ? 'Rates Provided' : pr.status}
                            </Badge>
                          </td>
                          <td className="py-3">
                            {pr.status === 'pending' ? (
                              <Button size="sm" onClick={() => openRatesDialog(pr)} data-testid={`provide-rates-${pr.id}`}>
                                <DollarSign className="h-4 w-4 mr-1" />
                                Provide Rates
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {(pr.supplier_rates || []).length} rate(s) submitted
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Provide Rates Dialog */}
      <Dialog open={isRatesDialogOpen} onOpenChange={setIsRatesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Provide Supplier Rates</DialogTitle>
          </DialogHeader>
          {selectedPR && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-muted/50 rounded-lg text-sm space-y-1">
                <p><span className="text-muted-foreground">Client:</span> <span className="font-medium">{selectedPR.client_name}</span></p>
                <p><span className="text-muted-foreground">Requirement:</span> {selectedPR.quantity}x {selectedPR.asset_type} {selectedPR.with_operator ? '(with operator)' : ''}</p>
                {selectedPR.requirement && <p className="text-muted-foreground text-xs">{selectedPR.requirement}</p>}
              </div>
              <div>
                <Label>Supplier * <span className="text-xs text-muted-foreground font-normal">(type to search or add new)</span></Label>
                <Input
                  list="pr-supplier-list"
                  value={ratesForm.supplier_name}
                  onChange={(e) => {
                    const typed = e.target.value;
                    const match = suppliers.find(s => s.name === typed);
                    setRatesForm({
                      ...ratesForm,
                      supplier_name: typed,
                      supplier_id: match ? match.id : '',
                    });
                  }}
                  placeholder="Start typing a supplier name..."
                  data-testid="rates-supplier-input"
                />
                <datalist id="pr-supplier-list">
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.name} />
                  ))}
                </datalist>
                {ratesForm.supplier_name && !ratesForm.supplier_id && (
                  <p className="text-xs text-amber-600 mt-1">
                    New supplier &quot;{ratesForm.supplier_name}&quot; — will be saved with this rate.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Daily Rate (SAR)</Label>
                  <Input type="number" value={ratesForm.daily_rate} onChange={(e) => setRatesForm({ ...ratesForm, daily_rate: e.target.value })} data-testid="rates-daily-input" />
                </div>
                <div>
                  <Label>Monthly Rate (SAR)</Label>
                  <Input type="number" value={ratesForm.monthly_rate} onChange={(e) => setRatesForm({ ...ratesForm, monthly_rate: e.target.value })} data-testid="rates-monthly-input" />
                </div>
                <div>
                  <Label>Mobilization (SAR)</Label>
                  <Input type="number" value={ratesForm.mobilization} onChange={(e) => setRatesForm({ ...ratesForm, mobilization: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={ratesForm.notes} onChange={(e) => setRatesForm({ ...ratesForm, notes: e.target.value })} rows={2} placeholder="Availability, conditions..." />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsRatesDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleProvideRates} data-testid="submit-rates-btn">Submit Rates</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Purchase Order (Supplier)</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Operating Company *</Label>
                <Select 
                  value={formData.company_id} 
                  onValueChange={(v) => setFormData({ ...formData, company_id: v, supplier_id: '' })}
                >
                  <SelectTrigger data-testid="select-company">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <Select 
                  value={formData.supplier_id} 
                  onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}
                  disabled={!formData.company_id}
                >
                  <SelectTrigger data-testid="select-supplier">
                    <SelectValue placeholder={formData.company_id ? "Select supplier" : "Select company first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getFilteredSuppliers().map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                    {getFilteredSuppliers().length === 0 && formData.company_id && (
                      <div className="p-2 text-sm text-muted-foreground">
                        No suppliers for this company
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Related Contract (Optional)</Label>
                <Select value={formData.contract_id || 'none'} onValueChange={(v) => setFormData({ ...formData, contract_id: v === 'none' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select contract" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Contract</SelectItem>
                    {contracts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.contract_number} - {c.client_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Select 
                  value={formData.payment_terms} 
                  onValueChange={(v) => setFormData({ ...formData, payment_terms: v })}
                >
                  <SelectTrigger data-testid="select-payment-terms">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STANDARD_PAYMENT_TERMS.map((term) => (
                      <SelectItem key={term.value} value={term.value}>{term.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.payment_terms === 'custom' && (
                <div className="space-y-2">
                  <Label>Custom Payment Days</Label>
                  <Input
                    type="number"
                    placeholder="Enter number of days"
                    value={formData.custom_payment_days}
                    onChange={(e) => setFormData({ ...formData, custom_payment_days: e.target.value })}
                    data-testid="custom-payment-days"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Delivery Location</Label>
                <Input
                  value={formData.delivery_location}
                  onChange={(e) => setFormData({ ...formData, delivery_location: e.target.value })}
                  placeholder="Site address for equipment delivery"
                />
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <Label className="text-base font-semibold">Line Items *</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>
              </div>

              {/* Custom Asset Type Input */}
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Add custom asset type..."
                  value={newCustomAssetType}
                  onChange={(e) => setNewCustomAssetType(e.target.value)}
                  className="max-w-xs"
                />
                <Button type="button" variant="secondary" size="sm" onClick={addCustomAssetType}>
                  Add Type
                </Button>
              </div>

              {formData.items.length === 0 ? (
                <div className="text-center py-6 border rounded-lg text-muted-foreground">
                  No items added. Click &quot;Add Item&quot; to add equipment.
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <Card key={index}>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-6 gap-3">
                          <div className="col-span-2">
                            <Select 
                              value={item.asset_type} 
                              onValueChange={(v) => updateItem(index, 'asset_type', v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Asset Type *" />
                              </SelectTrigger>
                              <SelectContent>
                                {getAllAssetTypes().map((type) => (
                                  <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                                <SelectItem value="custom">+ Custom Type</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {item.asset_type === 'custom' && (
                            <div className="col-span-2">
                              <Input
                                placeholder="Enter custom asset type"
                                value={item.custom_asset_type || ''}
                                onChange={(e) => updateItem(index, 'custom_asset_type', e.target.value)}
                              />
                            </div>
                          )}
                          
                          <Input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                          />
                          <Input
                            type="number"
                            placeholder="Unit Rate"
                            value={item.unit_rate}
                            onChange={(e) => updateItem(index, 'unit_rate', parseFloat(e.target.value) || 0)}
                          />
                          <div className="flex items-center font-semibold">
                            SAR {item.total?.toLocaleString() || 0}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeItem(index)}
                            className="text-red-500"
                          >
                            ×
                          </Button>
                          <div className="col-span-6">
                            <Input
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Totals */}
            {formData.items.length > 0 && (
              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>SAR {calculateTotals().subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>VAT (15%):</span>
                      <span>SAR {calculateTotals().vatAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span>SAR {calculateTotals().total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Terms */}
            <div className="space-y-2">
              <Label>Additional Payment / Customized Terms</Label>
              <Textarea
                value={formData.additional_terms}
                onChange={(e) => setFormData({ ...formData, additional_terms: e.target.value })}
                placeholder="Enter any additional payment terms or special conditions..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes or instructions..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-po-btn">Create Purchase Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
          </DialogHeader>
          
          {selectedPO && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold">{selectedPO.po_number}</h3>
                  <p className="text-muted-foreground">{selectedPO.supplier_name}</p>
                  <p className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                    <Building2 className="h-4 w-4" />
                    Operating Company: {getCompanyName(selectedPO.company_id)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(selectedPO.status)}
                  <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(selectedPO)}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Start Date</p>
                  <p className="font-medium">{new Date(selectedPO.start_date).toLocaleDateString()}</p>
                </div>
                {selectedPO.end_date && (
                  <div>
                    <p className="text-muted-foreground">End Date</p>
                    <p className="font-medium">{new Date(selectedPO.end_date).toLocaleDateString()}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Payment Terms</p>
                  <p className="font-medium">{selectedPO.payment_terms}</p>
                </div>
                {selectedPO.delivery_location && (
                  <div>
                    <p className="text-muted-foreground">Delivery Location</p>
                    <p className="font-medium">{selectedPO.delivery_location}</p>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-semibold mb-2">Items</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Description</th>
                      <th className="text-center py-2">Qty</th>
                      <th className="text-right py-2">Rate</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items?.map((item, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2">{item.description || item.asset_type}</td>
                        <td className="text-center py-2">{item.quantity}</td>
                        <td className="text-right py-2">SAR {item.unit_rate?.toLocaleString()}</td>
                        <td className="text-right py-2">SAR {item.total?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>SAR {selectedPO.subtotal?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>VAT ({selectedPO.vat_rate}%):</span>
                      <span>SAR {selectedPO.vat_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span>SAR {selectedPO.total?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedPO.additional_terms && (
                <div>
                  <h4 className="font-semibold mb-1">Additional Terms</h4>
                  <p className="text-sm text-muted-foreground">{selectedPO.additional_terms}</p>
                </div>
              )}

              {selectedPO.notes && (
                <div>
                  <h4 className="font-semibold mb-1">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedPO.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Received PO Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Client Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a purchase order received from a client. This helps track incoming orders separately from supplier POs.
            </p>
            
            <div className="space-y-2">
              <Label>Operating Company</Label>
              <Select 
                value={companyFilter !== 'all' ? companyFilter : companies[0]?.id || ''} 
                onValueChange={(v) => setCompanyFilter(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>PO Document (PDF/Image)</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleUploadReceivedPO(e.target.files[0]);
                  }
                }}
                data-testid="upload-po-file"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;
